import { type ReactNode, useEffect, useState } from 'react';
import {
  EventType,
  type EventMessage,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { identifyLogRocketUser } from '@/config/logrocketIdentity';
import { msalInstance } from './msalConfig';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Initialises a single `PublicClientApplication` (shared with the API client
 * via the module-level singleton in `msalConfig.ts`), handles the redirect
 * promise once on mount, and exposes the MSAL React context to the rest of
 * the app.
 *
 * Initialisation is deliberately async: MSAL v3 requires `initialize()` to
 * resolve before any other API call (including `handleRedirectPromise`). We
 * render a small placeholder until that completes to avoid a flash of the
 * unauthenticated UI when the user is in fact returning from a redirect.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const instance = msalInstance;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      await instance.initialize();

      const result = await instance.handleRedirectPromise();
      if (result?.account) {
        instance.setActiveAccount(result.account);
      } else {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0 && !instance.getActiveAccount()) {
          instance.setActiveAccount(accounts[0]);
        }
      }
      // Associate the LogRocket session with the account (oid only, no PII).
      // Covers both a fresh redirect and a cached session on reload.
      identifyLogRocketUser(instance.getActiveAccount());

      if (!cancelled) setReady(true);
    };

    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS &&
        event.payload &&
        'account' in event.payload &&
        (event.payload as AuthenticationResult).account
      ) {
        const account = (event.payload as AuthenticationResult).account;
        instance.setActiveAccount(account);
        identifyLogRocketUser(account);
      }
    });

    bootstrap().catch((err) => {
      console.error('[auth] MSAL initialisation failed', err);
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  if (!ready) {
    return null;
  }

  return <MsalProvider instance={instance}>{children}</MsalProvider>;
}
